import { useContext, useMemo, useState } from 'react';
import { useQuery, useLazyQuery } from '@apollo/client';
import {
  Table,
  Loader,
  IColumn,
  SvgIcon,
  LabelWithIcon,
  IDashboardLoginActivities,
} from '@ascd/witsby-components';
import { Box, Grid, Tooltip, IconButton } from '@mui/material';
import * as FileSaver from 'file-saver';
import { debounce, get, isEmpty } from 'lodash';
import { DateTime } from 'luxon';
import * as XLSX from 'xlsx';
import { ASCD_ID } from '@constants';
import { AppContext } from '@contexts/appContext';
import { DashboardContext, eDashboardActionType } from '@contexts/dashboardContext';
import DASHBOARD_LOGIN_ACTIVITY from '@graphql/schema/dashboardLoginActivity.graphql';
import { ITableFilter } from '@types';
import { dateFormat, handleGraphqlError, scrollToTop, showToast } from '@utils';

const LoginActivity = () => {
  const {
    state: { currentInstitution },
  } = useContext(AppContext);

  const {
    dispatch,
    state: { sideBarApiFilter },
  } = useContext(DashboardContext);

  const {
    assignedBy,
    internalId1,
    selectedGroup,
    selectedInstitution,
    createdByOrganizationId,
    dates: { startDate, endDate },
  } = sideBarApiFilter;

  const [filter, setFilter] = useState<ITableFilter>({
    page: 0,
    limit: 20,
    searchText: '',
    sortDirection: 'DESC',
    sortField: 'lastAccess',
    searchField: ['userInfo.name'],
  });

  // Handle institution selection filter for ASCD admins
  const currentSelectedInstitution = useMemo(() => {
    if (!isEmpty(selectedInstitution)) return selectedInstitution;

    return {
      value: currentInstitution._id,
      label: currentInstitution.name,
    };
  }, [currentInstitution._id, currentInstitution.name, selectedInstitution]);

  const variables = {
    endDate,
    startDate,
    assignedBy,
    titles: null,
    managerIds: null,
    organizationId:
      currentSelectedInstitution?.label === 'ALL' ? '' : currentSelectedInstitution.value,
    createdByOrganizationId: isEmpty(createdByOrganizationId)
      ? [currentSelectedInstitution.value, ASCD_ID]
      : createdByOrganizationId,
    schoolIds:
      selectedGroup.length > 0 ||
      currentSelectedInstitution?.label === 'ALL' ||
      currentSelectedInstitution?.label === 'ASCD'
        ? null
        : internalId1.map((internalId) => internalId?.internalId),
    groupIds: (selectedGroup || []).map((val) => val._id),
    filter,
  };

  const handleLoginActivityUserDetails = (data: IDashboardLoginActivities) => {
    scrollToTop();
    dispatch({
      type: 'DETAILS_PAGE' as eDashboardActionType.DETAILS_PAGE,
      data: {
        type: 'LOGIN_ACTIVITY_USER_DETAILS',
        detailsPageData: { data },
      },
    });
  };

  const loginActivityColumn: IColumn[] = [
    {
      key: 'userName',
      name: 'Learner Name',
      width: '35%',
      isSorting: true,
      ...(filter.sortField === 'userName' && {
        sortType: filter.sortDirection,
      }),
      render: (_, value) => (
        <Box
          tabIndex={0}
          sx={{
            cursor: 'pointer',
            '&:hover,&:focus': {
              textDecoration: 'underline',
              outline: 'none',
              color: (theme) => theme.palette.primary.light,
            },
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              handleLoginActivityUserDetails(value as IDashboardLoginActivities);
            }
          }}
          onClick={() => handleLoginActivityUserDetails(value as IDashboardLoginActivities)}>
          <Tooltip describeChild title={(value as IDashboardLoginActivities)?.email} arrow>
            <span>{(value as IDashboardLoginActivities)?.userName}</span>
          </Tooltip>
        </Box>
      ),
    },
    {
      key: 'email',
      name: 'Email',
      isSorting: true,
      ...(filter.sortField === 'email' && {
        sortType: filter.sortDirection,
      }),
    },
    {
      key: 'schoolName',
      name: 'School Name',
      width: '35%',
      align: 'center',
      headerAlign: 'center',
      isSorting: true,
      ...(filter.sortField === 'schoolName' && {
        sortType: filter.sortDirection,
      }),
    },
    {
      key: 'loginCount',
      name: 'Login Count',
      align: 'center',
      headerAlign: 'center',
      isSorting: true,
      ...(filter.sortField === 'loginCount' && {
        sortType: filter.sortDirection,
      }),
    },

    {
      key: 'lastAccess',
      name: 'Last Login',
      isSorting: true,
      ...(filter.sortField === 'lastAccess' && {
        sortType: filter.sortDirection,
      }),
      render: (_, value) => (
        <Box>
          {DateTime.fromJSDate(
            new Date((value as IDashboardLoginActivities)?.lastAccess as string),
          ).toFormat(`''LL'/'dd'/'yyyy'`)}
        </Box>
      ),
    },
  ];

  const { loading, data } = useQuery(DASHBOARD_LOGIN_ACTIVITY, {
    variables,
    fetchPolicy: 'network-only',
  });
  const [dashboardLoginActivityCSV, { loading: isCSVLoading }] = useLazyQuery(
    DASHBOARD_LOGIN_ACTIVITY,
    { fetchPolicy: 'network-only' },
  );

  const dashboardLoginActivity = get(data, 'dashboardLoginActivity', {
    totalCount: 0,
    loginActivities: [],
  });

  const exportToCSV = async () => {
    if (!isCSVLoading) {
      dashboardLoginActivityCSV({
        variables: {
          ...variables,
          filter: {
            ...filter,
            limit: dashboardLoginActivity.totalCount,
            page: 0,
          },
        },
      })
        .then((res) => {
          const datalist = res.data.dashboardLoginActivity.loginActivities.map(
            (item: IDashboardLoginActivities) => ({
              'Learner Name': item.userName,
              'Learner Email': item.email,
              'Login Count': item.loginCount,
              'School Name': item.schoolName,
              'Last Login': dateFormat({ date: item.lastAccess, format: 'dd-MMM-yyyy' }),
            }),
          );
          const fileType =
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
          const fileExtension = '.xlsx';
          const ws = XLSX.utils.json_to_sheet(datalist);
          const wb = { Sheets: { data: ws }, SheetNames: ['data'] };
          const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
          const excelData = new Blob([excelBuffer], { type: fileType });
          FileSaver.saveAs(
            excelData,
            `Login Activity (${dateFormat({ date: startDate, format: 'dd-MMM-yyyy' })} - ${dateFormat(
              { date: endDate, format: 'dd-MMM-yyyy' },
            )}) ${fileExtension}`,
          );
          showToast('Downloaded Successfully');
        })
        .catch(handleGraphqlError);
    }
  };

  return (
    <Box sx={{ mt: 1, mb: 5 }}>
      <Box>
        <Grid container spacing={3}>
          <Grid item xl={12} sm={12} xs={12}>
            <Table
              isSearchEnable
              searchPlaceHolder="Search Learner"
              apiPaginationEnable
              loading={loading}
              columns={loginActivityColumn}
              data={dashboardLoginActivity.loginActivities}
              count={dashboardLoginActivity.totalCount}
              page={filter.page}
              pageSize={filter.limit}
              headerChildren={
                <LabelWithIcon
                  iconName="MOVE_UP"
                  iconDirection="left"
                  title={`Login Activity from ${DateTime.fromJSDate(new Date(startDate))?.toFormat(
                    `''LLLL' 'dd', 'yyyy''`,
                  )} to ${DateTime.fromJSDate(new Date(endDate))?.toFormat(
                    `''LLLL' 'dd', 'yyyy''`,
                  )}`}
                  IconTitleDistance={1}
                  iconColorHex="#FFFFFF"
                  sx={{ color: '#FFFFFF' }}
                  onClick={() => {
                    dispatch({
                      type: 'DETAILS_PAGE' as eDashboardActionType.DETAILS_PAGE,
                      data: null,
                    });
                  }}
                  iconSx={{ transform: 'rotate(270deg)' }}
                />
              }
              dataEmptyChildren={<Box>No login activity found.</Box>}
              searchValue={filter.searchText}
              activeSortField={filter.sortField}
              handlePageChange={(_, newPage) => {
                setFilter({ ...filter, page: newPage });
              }}
              onHandleSearch={debounce((e) => {
                setFilter({ ...filter, page: 0, searchText: e.target.value });
              }, 500)}
              handleRowsPerPageChange={(e) => {
                setFilter({ ...filter, page: 0, limit: parseInt(e.target.value, 10) });
              }}
              onSortClick={(key, sortType) => {
                setFilter({
                  ...filter,
                  page: 0,
                  sortField: key as string,
                  sortDirection: sortType,
                });
              }}
              headerExtra={
                dashboardLoginActivity.totalCount > 0 && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                    }}>
                    {isCSVLoading ? (
                      <Loader
                        size={20}
                        sx={{ color: '#FFFFFF', ml: (theme) => theme.spaces.LARGE }}
                      />
                    ) : (
                      <Tooltip describeChild title="Download the report below" arrow>
                        <IconButton
                          sx={{
                            ml: (theme) => theme.spaces.REGULAR,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                          }}
                          onClick={() => exportToCSV()}
                          aria-label="login activity">
                          <SvgIcon
                            icon="FILE_DOWNLOAD"
                            viewBox="0 0 22 29"
                            sx={{ fill: '#FFFF' }}
                          />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                )
              }
            />
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default LoginActivity;
